<main class="upgrade-page" aria-labelledby="upgrade-title">
  <button mat-stroked-button type="button" class="back-button" (click)="goBack()">
    <mat-icon>arrow_back</mat-icon>{{ "open_jobs_list_back" | translate }}
  </button>

  @if (isLoading) {
    <div class="upgrade-state"><mat-spinner diameter="36"></mat-spinner></div>
  } @else if (loadErrorKey) {
    <div class="upgrade-state upgrade-state--error" role="alert">
      <mat-icon>error_outline</mat-icon><span>{{ loadErrorKey | translate }}</span>
    </div>
  } @else if (job) {
    <header class="upgrade-header">
      <div>
        <span>{{ siteName }}</span>
        <h1 id="upgrade-title">{{ "job_upgrade_title" | translate: { referenceNumber: job.referenceNumber } }}</h1>
      </div>
      <span class="status-label">{{ job.jobStatusName }}</span>
    </header>

    <p class="upgrade-intro">
      {{ (upgradeThreshold > 0 ? "job_upgrade_intro_threshold" : "job_upgrade_intro") | translate: { currency: currency, threshold: upgradeThreshold } }}
    </p>

    <form [formGroup]="form" (ngSubmit)="submit()" novalidate>
      <section class="upgrade-details" aria-labelledby="upgrade-details-title">
        <div class="section-heading">
          <h2 id="upgrade-details-title">{{ "job_upgrade_details" | translate }}</h2>
          <span>{{ "job_upgrade_required_hint" | translate }}</span>
        </div>

        <dl class="job-facts">
          <div><dt>{{ "job_upgrade_job" | translate }}</dt><dd>{{ job.referenceNumber }}</dd></div>
          <div><dt>{{ "job_upgrade_site" | translate }}</dt><dd>{{ siteName }}</dd></div>
          <div><dt>{{ "job_upgrade_contractor" | translate }}</dt><dd>{{ job.allocatedUserEmail || ("open_jobs_summary_not_available" | translate) }}</dd></div>
        </dl>

        <div class="details-grid">
          <mat-form-field appearance="outline">
            <mat-label>{{ "job_upgrade_contact_name" | translate }}</mat-label>
            <input matInput formControlName="contactName" autocomplete="name">
            <mat-error>{{ "job_upgrade_contact_name_required" | translate }}</mat-error>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{ "job_upgrade_contact_number" | translate }}</mat-label>
            <input matInput formControlName="contactNumber" inputmode="tel" autocomplete="tel">
            <mat-error>{{ "job_upgrade_contact_number_required" | translate }}</mat-error>
          </mat-form-field>
          <mat-form-field appearance="outline" class="description-field">
            <mat-label>{{ "job_upgrade_description" | translate }}</mat-label>
            <textarea matInput rows="5" formControlName="descriptionOfWorks"></textarea>
            <mat-error>{{ "job_upgrade_description_required" | translate }}</mat-error>
          </mat-form-field>
        </div>

        <div class="attachments">
          <strong>{{ "job_upgrade_attachments" | translate }}</strong>
          <label class="attachment-button">
            <mat-icon>attach_file</mat-icon>{{ "job_upgrade_add_attachment" | translate }}
            <input type="file" multiple (change)="selectAttachments($event)">
          </label>
          @for (attachment of attachments; track attachment.name) {
            <div class="attachment-file">
              <span>{{ attachment.name }}</span>
              <button mat-icon-button type="button" (click)="removeAttachment(attachment)" [attr.aria-label]="'job_upgrade_remove_attachment' | translate"><mat-icon>delete</mat-icon></button>
            </div>
          }
        </div>
      </section>

      <section class="expenses" aria-labelledby="expenses-title">
        <div class="section-heading section-heading--actions">
          <div>
            <h2 id="expenses-title">{{ "job_upgrade_expenses" | translate }}</h2>
            <span>{{ "job_upgrade_expenses_hint" | translate }}</span>
          </div>
          <button mat-stroked-button type="button" (click)="openLineItem()">
            <mat-icon>add</mat-icon>{{ "job_upgrade_add_expense" | translate }}
          </button>
        </div>

        @if (lineItems.length === 0) {
          <div class="empty-expenses">
            <mat-icon>receipt_long</mat-icon>
            <span>{{ "job_upgrade_no_expenses" | translate }}</span>
          </div>
        } @else {
          <div class="expense-table">
            <div class="expense-row expense-row--header" aria-hidden="true">
              <span>{{ "job_upgrade_item" | translate }}</span>
              <span>{{ "job_upgrade_description_short" | translate }}</span>
              <span>{{ "job_upgrade_unit_price" | translate }}</span>
              <span>{{ "job_upgrade_quantity" | translate }}</span>
              <span>{{ "job_upgrade_tax" | translate }}</span>
              <span>{{ "job_upgrade_total" | translate }}</span>
              <span>{{ "job_upgrade_actions" | translate }}</span>
            </div>
            @for (lineItem of lineItems; track $index) {
              <article class="expense-row">
                <span data-label="Item">{{ lineItem.itemType }}</span>
                <span data-label="Description">{{ lineItem.itemDescription }}</span>
                <span data-label="Unit price">{{ currency }}{{ lineItem.unitPrice | number: '1.2-2' }}</span>
                <span data-label="Quantity">{{ lineItem.itemQuantity | number: '1.0-2' }}</span>
                <span data-label="Tax">{{ currency }}{{ lineItem.lineTax | number: '1.2-2' }}</span>
                <strong data-label="Total">{{ currency }}{{ lineItem.lineGross | number: '1.2-2' }}</strong>
                <span class="expense-actions">
                  <button mat-icon-button type="button" (click)="openLineItem(lineItem)" [attr.aria-label]="'job_upgrade_edit_expense' | translate"><mat-icon>edit</mat-icon></button>
                  <button mat-icon-button type="button" (click)="deleteLineItem(lineItem)" [attr.aria-label]="'job_upgrade_delete_expense' | translate"><mat-icon>delete</mat-icon></button>
                </span>
              </article>
            }
          </div>

          <dl class="totals">
            <div><dt>{{ "job_upgrade_net" | translate }}</dt><dd>{{ currency }}{{ totalNet | number: '1.2-2' }}</dd></div>
            <div><dt>{{ "job_upgrade_tax" | translate }}</dt><dd>{{ currency }}{{ totalTax | number: '1.2-2' }}</dd></div>
            <div class="totals__gross"><dt>{{ "job_upgrade_gross" | translate }}</dt><dd>{{ currency }}{{ totalGross | number: '1.2-2' }}</dd></div>
          </dl>
        }
      </section>

      @if (!canSubmit) {
        <p class="submission-hint"><mat-icon>info</mat-icon>{{ "job_upgrade_submission_hint" | translate: { currency: currency, threshold: upgradeThreshold } }}</p>
      }
      @if (submitErrorKey) {
        <div class="form-message" role="alert">{{ submitErrorKey | translate }}</div>
      }

      <div class="form-actions">
        <button mat-stroked-button type="button" (click)="print()"><mat-icon>print</mat-icon>{{ "job_upgrade_print" | translate }}</button>
        <button mat-raised-button type="submit" [disabled]="!canSubmit">
          <mat-icon>send</mat-icon>{{ (isSaving ? "job_upgrade_submitting" : "job_upgrade_submit") | translate }}
        </button>
      </div>
    </form>
  }
</main>